import type { ForwardedRef } from 'react';
import type { NotificationProps } from '@progress/kendo-react-notification';
import type { ToastType } from '@ricoh/ria.client.core/plugins/store/slices/component/popup/toast';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useImperativeHandle, useMemo } from 'react';
import { useCoreStoreDispatch } from '@ricoh/ria.client.core/plugins/store/hooks/dispatch';
import { useCoreStoreSelector } from '@ricoh/ria.client.core/plugins/store/hooks/selector';
import { componentPopupSliceActions } from '@ricoh/ria.client.core/plugins/store/slices/component/popup';

import type { ToastHooks, ToastProps, ToastRef } from './_.type';

/**
 * Use Toast hooks.
 *
 * @param {ToastProps} _props Toast props.
 * @param {ToastRef} ref Toast ref.
 *
 * @returns {ToastHooks} Toast hooks.
 */
export default function useToastHooks(_props: ToastProps, ref: ForwardedRef<ToastRef>): ToastHooks {
  const dispatch = useCoreStoreDispatch();

  const items = useCoreStoreSelector((state) => state.component_popup.toast.states);

  const iconName = useCallback<Func<[type: ToastType], string>>(
    (type) =>
      ({
        info: 'info',
        success: 'check_circle',
        warning: 'warning',
        error: 'error',
      })[type],
    [],
  );

  const onClose = useCallback<Func<[key: string], NotificationProps['onClose']>>(
    (key) => () => {
      dispatch(componentPopupSliceActions.toast_pop(key));
    },
    [dispatch],
  );

  useImperativeHandle(ref, () => ({}));

  return useMemo<ToastHooks>(
    () => ({
      refs: {},

      states: {
        items,

        iconName,
      },

      events: {
        onClose,
      },
    }),
    [iconName, items, onClose],
  );
}
